import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, TextInput} from '@techsmith/tsc-cloud-style-guide';
import conversationApi from '../../service/conversationApi';
import {StyledWarning, MinWidthButtonWrapper} from '../util/StyledElements';
import '../../static/css/modal.less';
import config from '../../service/config';
import cssConstants from '../../constants/cssConstants';
import {IConversationData} from '../../model/conversationModel';
import {IWorkerState} from '../../model/workerState';
import {conversationsStore, updateConversationsState} from '../../context/conversationsProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {IFieldData} from '../../model/fieldData';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';

export const CreateConversationModalBase: React.FC<IStateMappedProps & ICreateConversationModalProps> = ({conversations, conversation, onClose, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [setCreateConversationState, updateCreateConversationState] = useState({isWorking: false, hasError: false} as IWorkerState);
   const [conversationTitle, setConversationTitle] = useState<IFieldData>({value: (conversation && conversation.Title) || '', errorText: ''});

   const renameConversation = async () => {
      const newConversation = await conversationApi.renameConversation(Hash, conversation.ConversationHash, conversationTitle.value);
      const conversationsCopy = Object.assign([], conversations);
      conversationsCopy.splice(conversationsCopy.findIndex(oldConversation => oldConversation.ConversationHash === conversation.ConversationHash), 1, newConversation);
      updateConversationsState({conversations: conversationsCopy});
   };

   const createConversation = async () => {
      const newConversation = await conversationApi.createConversation(Hash, conversationTitle.value);
      conversations.push(newConversation);
      updateConversationsState({conversations: conversations});
      updateSelectedConversationState({Title: newConversation.Title, VideoReviewId: newConversation.VideoReviewId, ConversationHash: newConversation.ConversationHash, SelectedComment: null});
   };

   const updateTitle = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      const newTitle = evt.target.value;
      const isEmpty = !newTitle.trim();
      const isTooLong = newTitle.length > config.watchConfig?.maxConversationTitleLength;

      setConversationTitle({
         ...conversationTitle,
         value: newTitle,
         errorText: isEmpty
            ? t('details.title.requiredError')
            : isTooLong
               ? t('details.title.maxLengthError', {0: config.watchConfig?.maxConversationTitleLength})
               : ''
      });
   };

   const conversationTitleNotChanged = conversation && conversation.Title === conversationTitle.value;
   const canSubmitTitle = !(conversationTitleNotChanged || setCreateConversationState.isWorking || conversationTitle.value.length === 0 || conversationTitle.errorText.length !== 0);

   const onPrimaryButtonClick = async () => {
      if (canSubmitTitle) {
         try {
            updateCreateConversationState({...setCreateConversationState, isWorking: true, hasError: false});
            if (conversation) {
               await renameConversation();
            } else {
               await createConversation();
            }
            onClose();
         } catch {
            updateCreateConversationState({...setCreateConversationState, isWorking: false, hasError: true});
         }
      }
   };

   const modalTitle = conversation ? t('conversations.renameConversationTitle') : t('conversations.addConversationTitle');

   return (
      <BasicModalPortal
         visible={true}
         title={modalTitle}
         width={cssConstants.defaultModalWidth}
         onClose={setCreateConversationState.isWorking ? (): void => null : onClose}
         themeName={theme}
         isLoading={setCreateConversationState.isWorking}
      >
         <div
            className="modal-body"
            data-test-id="create-conversation-modal"
         >
            <div className="form-body themeable-section">
               <div className="form-field-group">
                  {setCreateConversationState.hasError && (
                     <StyledWarning>
                        {t('general.errorText')}
                     </StyledWarning>
                  )}
                  <label>
                     {t('conversations.inputLabel')}
                     <TextInput
                        placeholder={t('details.title.placeholder', {0: config.watchConfig?.maxConversationTitleLength})}
                        value={conversationTitle.value}
                        errorText={conversationTitle.errorText}
                        onChange={updateTitle}
                        onEnter={onPrimaryButtonClick}
                        themeName={theme}
                        ariaLabel="Conversation Title"
                        readOnly={false}
                        testId="conversation-title-input"
                        disabled={setCreateConversationState.isWorking}
                     />
                  </label>
               </div>
               {!setCreateConversationState.isWorking && (
                  <div className="button-group">
                     <MinWidthButtonWrapper>
                        <ControlButton
                           label={t('general.cancel')}
                           buttonType={TscButtonType.secondary}
                           disabled={setCreateConversationState.isWorking}
                           themeName={theme}
                           onClick={onClose}
                           testId="cancel-button"
                        />
                        <ControlButton
                           label={conversation ? t('general.save') : t('conversations.actions.add')}
                           buttonType={TscButtonType.primary}
                           disabled={!canSubmitTitle}
                           themeName={theme}
                           onClick={onPrimaryButtonClick}
                           testId="create-conversation-button"
                        />
                     </MinWidthButtonWrapper>
                  </div>
               )}
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IStateMappedProps {
   conversations: IConversationData[];
   Hash: string;
}

export interface ICreateConversationModalProps {
   conversation?: IConversationData;
   onClose(): void;
}

const mapStatesToProps = (
   {conversation, onClose}: ICreateConversationModalProps,
   {conversations}: Partial<IStateMappedProps>,
   {Hash}: Partial<IStateMappedProps>
): ICreateConversationModalProps & IStateMappedProps => ({conversations, conversation, onClose, Hash});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, mediaStore)(CreateConversationModalBase);
